import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getChinaLocalization } from 'shared/account/reducer';
import { T } from 'shared/helper/translate';
import featureEnabled from 'shared/helper/feature';
import { getPaddlePlanIdForCurrentPlan, getIsPastDue } from '../reducer';
import { PLAN_EXPIRATION } from '../constants';
import PlanManagementButton from '../plan/PlanManagementButton';
var RubriqNotificationBox = function RubriqNotificationBox() {
  var _curiePlans$premium;
  var curiePlan = useSelector(function (_ref) {
    var curie = _ref.curie;
    return curie.plan;
  });
  var curiePlans = useSelector(function (_ref2) {
    var curie = _ref2.curie;
    return curie.plans;
  });
  var planStatus = curiePlan.status,
    planType = curiePlan.planType;
  var isChinaUser = useSelector(getChinaLocalization);
  var paddleBillingForChina = featureEnabled('rubriq_china_also_uses_paddle');
  var useCartInsteadOfPaddle = isChinaUser && !paddleBillingForChina;
  // Payment recovery grace period: the new PaymentRecoveryBanner (workspace) and
  // PlanPagePaymentRecoveryBanner (My Plans) already handle this state. Suppress
  // the existing notification entirely so we don't show two banners.
  var isPastDue = useSelector(getIsPastDue);
  var upgradeLink = useCartInsteadOfPaddle ? '/rubriq/plan/subscribe' : '/rubriq/plan/subscribe/premium';
  var hasCrossedExpirationThreshold = function hasCrossedExpirationThreshold() {
    return curiePlan.endsAt && moment(curiePlan.endsAt) <= moment().add(PLAN_EXPIRATION.REMAINING_THRESHOLD, 'd');
  };
  var isActiveCartPurchaseSub = curiePlan.receiptCode && planStatus === 'active';

  // Trial past-due: payment failed while still in the trial grace period.
  // Identified by trialGracePeriod === true on the premium plans object (confirmed via API data).
  var isTrialPastDue = (curiePlans === null || curiePlans === void 0 || (_curiePlans$premium = curiePlans.premium) === null || _curiePlans$premium === void 0 ? void 0 : _curiePlans$premium.trialGracePeriod) === true;
  var notification = function () {
    // Payment recovery grace period — handled by PaymentRecoveryBanner (workspace)
    // and PlanPagePaymentRecoveryBanner (My Plans). Return null here so the old
    // curie.payment-failed-within-grace-period banner doesn't appear alongside them.
    if (isPastDue) return null;
    if (planType === 'premium') {
      var _curiePlans$premium2, _curiePlans$premium3, _curiePlans$premium4, _curiePlans$premium5, _curiePlans$premium6, _curiePlans$premium7, _curiePlans$premium8;
      // A non-trial cancellation still within the paid period has full access —
      // suppress the expiry banner until the paid period actually ends.
      // Exception: if pastDueStartedAt is set, the cancel followed a payment failure — show banner.
      var isCancelledNonTrialWithinPaidPeriod = planStatus === 'cancelled' && (curiePlans === null || curiePlans === void 0 || (_curiePlans$premium2 = curiePlans.premium) === null || _curiePlans$premium2 === void 0 ? void 0 : _curiePlans$premium2.cancelState) === 'nontrial' && (curiePlans === null || curiePlans === void 0 || (_curiePlans$premium3 = curiePlans.premium) === null || _curiePlans$premium3 === void 0 ? void 0 : _curiePlans$premium3.paymentStatus) === 'active' && moment(curiePlans === null || curiePlans === void 0 || (_curiePlans$premium4 = curiePlans.premium) === null || _curiePlans$premium4 === void 0 ? void 0 : _curiePlans$premium4.endsAt).utc().isAfter(moment().utc()) && !(curiePlans !== null && curiePlans !== void 0 && (_curiePlans$premium5 = curiePlans.premium) !== null && _curiePlans$premium5 !== void 0 && _curiePlans$premium5.pastDueStartedAt);

      // A trial cancellation still within the trial window also has access —
      // suppress the banner so they don't see a false "plan expired" message.
      var isCancelledTrialWithinTrialPeriod = planStatus === 'cancelled' && (curiePlans === null || curiePlans === void 0 || (_curiePlans$premium6 = curiePlans.premium) === null || _curiePlans$premium6 === void 0 ? void 0 : _curiePlans$premium6.cancelState) === 'trial' && (curiePlans === null || curiePlans === void 0 || (_curiePlans$premium7 = curiePlans.premium) === null || _curiePlans$premium7 === void 0 ? void 0 : _curiePlans$premium7.paymentStatus) === 'active' && moment(curiePlans === null || curiePlans === void 0 || (_curiePlans$premium8 = curiePlans.premium) === null || _curiePlans$premium8 === void 0 ? void 0 : _curiePlans$premium8.endsAt).utc().isAfter(moment().utc());

      // Cancelled plans past their end date are treated like expired:
      // show the "Purchase a Rubriq Premium plan" banner linking to the plans page.
      if ((planStatus === 'expired' || planStatus === 'cancelled') && !isCancelledNonTrialWithinPaidPeriod && !isCancelledTrialWithinTrialPeriod) {
        return /*#__PURE__*/React.createElement(T.md, {
          inline: true,
          useLink: true,
          source: "curie.premium-expired-notification",
          params: {
            link: '/rubriq/plan/subscribe'
          }
        });
      }
      if (isActiveCartPurchaseSub && hasCrossedExpirationThreshold()) {
        return /*#__PURE__*/React.createElement(T.md, {
          inline: true,
          useLink: true,
          source: "curie.premium-to-be-expired-notification",
          params: {
            link: '/rubriq/plan/subscribe'
          }
        });
      }
      // Trial past-due: payment failed while in trial grace period.
      // User loses all access immediately — prompt to update billing info, not start a new sub.
      if (!useCartInsteadOfPaddle && planStatus === 'error' && isTrialPastDue) {
        return /*#__PURE__*/React.createElement(T.md, {
          inline: true,
          source: "curie.payment-failed-within-grace-period",
          linkRenderer: function linkRenderer(_ref3) {
            var _curiePlan$paddleSubs;
            var children = _ref3.children;
            return /*#__PURE__*/React.createElement(PlanManagementButton, {
              classicUrl: (_curiePlan$paddleSubs = curiePlan.paddleSubscription) === null || _curiePlan$paddleSubs === void 0 ? void 0 : _curiePlan$paddleSubs.updateUrl,
              targetAction: "update",
              paddlePlanId: useSelector(getPaddlePlanIdForCurrentPlan),
              asButton: false
            }, children);
          }
        });
      }
      if (!useCartInsteadOfPaddle && planStatus === 'error') {
        return /*#__PURE__*/React.createElement(T.md, {
          inline: true,
          source: "curie.payment-failed-within-grace-period",
          linkRenderer: function linkRenderer(_ref4) {
            var _curiePlan$paddleSubs2;
            var children = _ref4.children;
            return /*#__PURE__*/React.createElement(PlanManagementButton, {
              classicUrl: (_curiePlan$paddleSubs2 = curiePlan.paddleSubscription) === null || _curiePlan$paddleSubs2 === void 0 ? void 0 : _curiePlan$paddleSubs2.updateUrl,
              targetAction: "update",
              paddlePlanId: useSelector(getPaddlePlanIdForCurrentPlan),
              asButton: false
            }, children);
          }
        });
      }
    }
    if (planType === 'free_trial' && planStatus === 'expired') {
      return /*#__PURE__*/React.createElement(T.md, {
        inline: true,
        useLink: true,
        source: "curie.free-trial-expired",
        params: {
          upgradeLink: upgradeLink
        }
      });
    }
    if (curiePlan.status === 'error') {
      return /*#__PURE__*/React.createElement(T.md, {
        inline: true,
        useLink: true,
        source: "curie.failed-payment-message",
        params: {
          upgradeLink: upgradeLink
        }
      });
    }
    return null;
  }();
  return notification ? /*#__PURE__*/React.createElement("div", {
    className: "row mb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-12"
  }, /*#__PURE__*/React.createElement("div", {
    className: "alert alert-block alert-warning"
  }, /*#__PURE__*/React.createElement("div", {
    className: "m-0"
  }, notification)))) : null;
};
export default RubriqNotificationBox;